/* Converto — global.js
   Composants transverses. Vanilla JS, custom elements, aucune dépendance. */

window.Converto = window.Converto || {};

/* ---------- Utilitaires ---------- */
/* Verrouillage d'un bouton pendant une requête : évite les doubles soumissions */
Converto.lockBtn = function (btn) {
  if (!btn) return;
  btn.disabled = true;
  btn.setAttribute('aria-busy', 'true');
  btn.classList.add('is-loading');
};
Converto.unlockBtn = function (btn) {
  if (!btn) return;
  btn.disabled = false;
  btn.removeAttribute('aria-busy');
  btn.classList.remove('is-loading');
};

/* Formatage monétaire aligné sur les huit formats Shopify officiels, pour que
   les montants réécrits en JS soient identiques au HTML rendu par | money.
   Les balises HTML éventuelles du format sont neutralisées (insertion en texte). */
Converto.formatMoney = function (cents) {
  const format = (window.themeSettings?.moneyFormat || '{{amount_with_comma_separator}} €').replace(/<[^>]*>/g, '');
  const value = cents / 100;
  const part = (decimals, thousands, decimalSep) => {
    const fixed = Math.abs(value).toFixed(decimals);
    const pieces = fixed.split('.');
    const int = pieces[0].replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
    const sign = value < 0 ? '-' : '';
    return sign + (decimals > 0 ? int + decimalSep + pieces[1] : int);
  };
  return format.replace(/\{\{\s*(\w+)\s*\}\}/g, (m, key) => {
    switch (key) {
      case 'amount': return part(2, ',', '.');
      case 'amount_no_decimals': return part(0, ',', '.');
      case 'amount_with_comma_separator': return part(2, '.', ',');
      case 'amount_no_decimals_with_comma_separator': return part(0, '.', ',');
      case 'amount_with_apostrophe_separator': return part(2, "'", '.');
      case 'amount_with_space_separator': return part(2, ' ', ',');
      case 'amount_no_decimals_with_space_separator': return part(0, ' ', ',');
      case 'amount_with_period_and_space_separator': return part(2, ' ', '.');
      default: return part(2, ',', '.');
    }
  });
};

/* R05 : même structure de prix après un changement de variante ou de plan.
   Les valeurs monétaires et références d’unité sont insérées comme texte. */
Converto.setPrice = function (element, variant) {
  if (!element) return;
  const append = (tag, className, text) => {
    const node = document.createElement(tag);
    if (className) node.className = className;
    node.textContent = text;
    element.append(node);
  };
  const sale = Number(variant.compare_at_price) > Number(variant.price);
  element.replaceChildren();
  append('span', sale ? 'price__sale' : '', Converto.formatMoney(variant.price));
  if (sale) append('s', 'price__compare', Converto.formatMoney(variant.compare_at_price));
  if (variant.unit_price > 0 && variant.unit_ref) append('span', 'price__unit', `${Converto.formatMoney(variant.unit_price)} / ${variant.unit_ref}`);
};

Converto.toast = function (message, { alert = false } = {}) {
  let toast = document.querySelector('.toast');
  if (!toast) {
    toast = document.createElement('div');
    toast.className = 'toast';
    document.body.appendChild(toast);
  }
  // Une erreur est annoncée immédiatement, une confirmation poliment
  toast.setAttribute('role', alert ? 'alert' : 'status');
  toast.setAttribute('aria-live', alert ? 'assertive' : 'polite');
  toast.textContent = message;
  toast.classList.add('is-visible');
  clearTimeout(toast._timer);
  toast._timer = setTimeout(() => toast.classList.remove('is-visible'), 2600);
};

/* Boucle de focus complète : focus initial, Tab/Shift+Tab bouclés.
   releaseFocus retire le piège à la fermeture. */
Converto.trapFocus = function (container, { initial = true } = {}) {
  const selector = 'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';
  const focusables = (root = container) => Array.from(root.querySelectorAll(selector))
    .filter((el) => el.offsetWidth > 0 || el.offsetHeight > 0 || el === document.activeElement);
  if (initial) {
    const list = focusables();
    if (list.length) list[0].focus();
  }
  if (container._trapHandler) container.removeEventListener('keydown', container._trapHandler);
  container._trapHandler = (e) => {
    if (e.key !== 'Tab') return;
    // Le panneau de variantes imbriqué garde sa propre boucle dans le tiroir.
    const items = focusables(e.target.closest('dialog[open]') || container);
    if (!items.length) return;
    const first = items[0];
    const last = items[items.length - 1];
    if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
    else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
  };
  container.addEventListener('keydown', container._trapHandler);
};
Converto.releaseFocus = function (container) {
  if (container && container._trapHandler) {
    container.removeEventListener('keydown', container._trapHandler);
    container._trapHandler = null;
  }
};

/* ---------- Règles de quantité Shopify ---------- */
Converto.quantity = {
  bounds(input) {
    const step = Math.max(1, Number(input.dataset.ruleStep) || 1);
    const ruleMin = Math.max(1, Number(input.dataset.ruleMin) || 1);
    const cartQuantity = Math.max(0, Number(input.dataset.variantCartQuantity) || 0);
    const line = input.hasAttribute('data-cart-quantity');
    const min = line || cartQuantity < ruleMin ? ruleMin : step;
    const ruleMax = input.dataset.ruleMax === '' ? null : Number(input.dataset.ruleMax);
    const otherQuantity = line ? Math.max(0, cartQuantity - Number(input.dataset.lineQuantity || 0)) : cartQuantity;
    const max = ruleMax === null || !Number.isFinite(ruleMax) ? null : Math.max(0, ruleMax - otherQuantity);
    return { min, max, step };
  },
  valid(value, { min, max, step }) {
    return Number.isInteger(value) && value >= min && (max === null || value <= max) && (value - min) % step === 0;
  },
  normalize(value, bounds) {
    const { min, max, step } = bounds;
    if (max !== null && max < min) return 0;
    let next = Number.isFinite(Number(value)) ? Math.max(min, Number(value)) : min;
    next = min + Math.ceil((next - min) / step) * step;
    if (max !== null) next = Math.min(next, min + Math.floor((max - min) / step) * step);
    return next;
  },
  refresh(input, quantity, { reset = false } = {}) {
    if (quantity) {
      input.dataset.ruleMin = quantity.rule?.min ?? 1;
      input.dataset.ruleMax = quantity.rule?.max ?? '';
      input.dataset.ruleStep = quantity.rule?.increment ?? 1;
      input.dataset.variantCartQuantity = quantity.cart_quantity || 0;
    }
    const bounds = this.bounds(input);
    input.min = bounds.min;
    input.step = bounds.step;
    if (bounds.max === null) input.removeAttribute('max');
    else input.max = bounds.max;
    if (!input.hasAttribute('data-cart-quantity')) input.value = this.normalize(reset ? bounds.min : input.value, bounds);
    input.closest('quantity-input')?.updateButtons?.();
    const container = input.closest('.quantity-field');
    if (!container) return bounds;
    container.querySelectorAll('[data-rule-label]').forEach((label) => {
      const type = label.dataset.ruleLabel;
      const value = type === 'min' ? Number(input.dataset.ruleMin) : type === 'increment' ? bounds.step : input.dataset.ruleMax;
      label.hidden = type === 'max' ? value === '' : value <= 1;
      label.textContent = label.dataset.template.replace('{quantity}', value);
    });
    const limit = container.querySelector('[data-quantity-limit]');
    if (limit) limit.hidden = !(bounds.max !== null && bounds.max < bounds.min);
    if (quantity) {
      const volume = container.querySelector('[data-volume-prices]');
      const list = volume?.querySelector('[data-volume-list]');
      if (volume && list) {
        const breaks = quantity.breaks || [];
        volume.hidden = !breaks.length;
        list.replaceChildren(...breaks.map((priceBreak) => {
          const item = document.createElement('li');
          item.textContent = list.dataset.template.replace('{quantity}', priceBreak.minimum_quantity).replace('{price}', Converto.formatMoney(priceBreak.price));
          return item;
        }));
      }
    }
    return bounds;
  },
  sync(root, variant) {
    if (!variant) return;
    root.querySelectorAll('input[data-quantity-variant]').forEach((input) => {
      const formId = input.form?.querySelector('[name="id"]')?.value;
      if (formId && String(formId) !== String(variant.id)) return;
      const changed = String(input.dataset.quantityVariant) !== String(variant.id);
      input.dataset.quantityVariant = variant.id;
      this.refresh(input, variant.quantity, { reset: changed });
    });
    this.syncTiers(root);
  },
  syncTiers(root) {
    const input = root.querySelector('form input[data-quantity-variant]');
    if (!input) return;
    const bounds = this.bounds(input);
    const hasNativePrices = !!root.querySelector('[data-volume-prices]:not([hidden])');
    root.querySelectorAll('quantity-breaks').forEach((tiers) => { tiers.hidden = hasNativePrices; });
    root.querySelectorAll('input[name="qb-offer"]').forEach((offer) => {
      offer.disabled = hasNativePrices || !this.valid(Number(offer.value), bounds);
      if (offer.disabled || Number(offer.value) !== Number(input.value)) {
        offer.checked = false;
        offer.closest('.quantity-break')?.classList.remove('is-selected');
      }
    });
  },
  updateCart(cart) {
    document.querySelectorAll('input[data-quantity-variant]').forEach((input) => {
      input.dataset.variantCartQuantity = cart.items.filter((item) => String(item.variant_id || item.id) === input.dataset.quantityVariant).reduce((sum, item) => sum + item.quantity, 0);
      this.refresh(input);
      const scope = input.closest('[data-product-scope]');
      if (scope) { this.syncTiers(scope); Converto.updateTotals?.(scope); }
    });
  }
};

/* ---------- Sélecteur de quantité ---------- */
class QuantityInput extends HTMLElement {
  connectedCallback() {
    this.input = this.querySelector('input');
    if (!this.input) return;
    this._change = () => {
      const bounds = Converto.quantity.bounds(this.input);
      this.input.value = Converto.quantity.normalize(this.input.value, bounds);
      this.updateButtons();
    };
    this._click = (event) => {
      const button = event.target.closest('button[data-action]');
      if (!button || button.disabled) return;
      event.preventDefault();
      const bounds = Converto.quantity.bounds(this.input);
      const current = Converto.quantity.normalize(this.input.value, bounds);
      const next = current + (button.dataset.action === 'increase' ? bounds.step : -bounds.step);
      this.input.value = Converto.quantity.normalize(Math.max(bounds.min, next), bounds);
      this.input.dispatchEvent(new Event('change', { bubbles: true }));
    };
    this.input.addEventListener('change', this._change);
    this.addEventListener('click', this._click);
    this.updateButtons();
  }
  disconnectedCallback() {
    this.input?.removeEventListener('change', this._change);
    this.removeEventListener('click', this._click);
  }
  updateButtons() {
    if (!this.input) return;
    const { min, max, step } = Converto.quantity.bounds(this.input);
    const current = Number(this.input.value);
    this.querySelector('[data-action="decrease"]')?.toggleAttribute('disabled', current <= min);
    this.querySelector('[data-action="increase"]')?.toggleAttribute('disabled', max !== null && current + step > max);
  }
}
customElements.define('quantity-input', QuantityInput);

/* ---------- Slider (scroll-snap + flèches) ---------- */
class SliderComponent extends HTMLElement {
  connectedCallback() {
    this._lifecycle?.abort();
    this._lifecycle = new AbortController();
    if (this.closest('[inert]')) return;
    this.track = this.querySelector('.slider');
    this.prev = this.querySelector('[data-slider-prev]');
    this.next = this.querySelector('[data-slider-next]');
    if (!this.track) return;
    this.prev?.addEventListener('click', () => this.scrollByPage(-1), { signal: this._lifecycle.signal });
    this.next?.addEventListener('click', () => this.scrollByPage(1), { signal: this._lifecycle.signal });
    this.track.addEventListener('scroll', () => this.updateButtons(), { signal: this._lifecycle.signal, passive: true });
    window.addEventListener('resize', () => this.updateButtons(), { signal: this._lifecycle.signal });
    this.bindDrag();
    this.updateButtons();
  }
  /* Défilement à la souris : cliquer, tenir, glisser. Le pointeur tactile garde
     le défilement natif, et un vrai glissement annule le clic qui suit. */
  bindDrag() {
    const signal = this._lifecycle.signal;
    const seuil = 6;
    let actif = false, glisse = false, departX = 0, departScroll = 0, pointeur = null;
    const fin = () => {
      if (!actif) return;
      actif = false;
      this.track.classList.remove('is-dragging');
      if (pointeur !== null) { try { this.track.releasePointerCapture(pointeur); } catch (e) {} pointeur = null; }
    };
    this.track.addEventListener('pointerdown', (event) => {
      if (event.pointerType === 'touch' || event.button !== 0) return;
      if (event.target.closest('input, select, textarea, label')) return;
      actif = true; glisse = false; departX = event.clientX; departScroll = this.track.scrollLeft; pointeur = event.pointerId;
    }, { signal });
    this.track.addEventListener('pointermove', (event) => {
      if (!actif) return;
      const delta = event.clientX - departX;
      if (!glisse) {
        if (Math.abs(delta) < seuil) return;
        glisse = true;
        this.track.classList.add('is-dragging');
        try { this.track.setPointerCapture(pointeur); } catch (e) {}
      }
      event.preventDefault();
      this.track.scrollLeft = departScroll - delta;
    }, { signal });
    this.track.addEventListener('pointerup', fin, { signal });
    this.track.addEventListener('pointercancel', fin, { signal });
    this.track.addEventListener('click', (event) => {
      if (!glisse) return;
      event.preventDefault();
      event.stopPropagation();
      glisse = false;
    }, { signal, capture: true });
  }
  disconnectedCallback() { this._lifecycle?.abort(); }
  scrollByPage(direction) {
    const slide = this.track.querySelector(':scope > *');
    const amount = slide ? slide.getBoundingClientRect().width + 24 : this.track.clientWidth;
    this.track.scrollBy({ left: amount * direction, behavior: 'smooth' });
  }
  updateButtons() {
    if (!this.prev && !this.next) return;
    const max = this.track.scrollWidth - this.track.clientWidth - 2;
    if (this.prev) this.prev.toggleAttribute('disabled', this.track.scrollLeft <= 2);
    if (this.next) this.next.toggleAttribute('disabled', this.track.scrollLeft >= max);
  }
}
customElements.define('slider-component', SliderComponent);

/* ---------- Tiroirs (menu mobile, panier) ---------- */
class DrawerToggle {
  static open(id) {
    const wrapper = document.getElementById(id);
    if (!wrapper) return;
    if (id === 'cart-drawer-wrapper' && Converto.popups && !Converto.popups.beforeCartOpen()) return;
    // Mémorise le déclencheur pour lui rendre le focus à la fermeture
    const wasOpen = wrapper.classList.contains('is-open');
    if (!wasOpen) wrapper._opener = document.activeElement;
    wrapper.classList.add('is-open');
    document.body.classList.add('drawer-open');
    document.querySelectorAll(`[data-drawer-open="${id}"]`).forEach((b) => b.setAttribute('aria-expanded', 'true'));
    const drawer = wrapper.querySelector('.drawer');
    if (drawer) {
      if (!drawer.hasAttribute('role')) drawer.setAttribute('role', 'dialog');
      drawer.setAttribute('aria-modal', 'true');
      Converto.trapFocus(drawer, { initial: !wasOpen });
    }
    document.dispatchEvent(new CustomEvent('drawer:open', { detail: { id } }));
  }
  static close(id) {
    const wrapper = document.getElementById(id);
    if (!wrapper) return;
    const hadFocus = wrapper.contains(document.activeElement);
    wrapper.classList.remove('is-open');
    document.body.classList.remove('drawer-open');
    document.querySelectorAll(`[data-drawer-open="${id}"]`).forEach((b) => b.setAttribute('aria-expanded', 'false'));
    const drawer = wrapper.querySelector('.drawer');
    if (drawer) Converto.releaseFocus(drawer);
    if (hadFocus && wrapper._opener && document.contains(wrapper._opener)) wrapper._opener.focus();
    wrapper._opener = null;
    document.dispatchEvent(new CustomEvent('drawer:close', { detail: { id } }));
  }
}
Converto.drawer = DrawerToggle;

// État initial des déclencheurs de tiroirs pour les lecteurs d'écran
document.querySelectorAll('[data-drawer-open]').forEach((b) => {
  if (!b.hasAttribute('aria-expanded')) b.setAttribute('aria-expanded', 'false');
});

document.addEventListener('click', (e) => {
  const opener = e.target.closest('[data-drawer-open]');
  if (opener) {
    e.preventDefault();
    DrawerToggle.open(opener.dataset.drawerOpen);
    return;
  }
  const closer = e.target.closest('[data-drawer-close]');
  if (closer) {
    e.preventDefault();
    DrawerToggle.close(closer.dataset.drawerClose);
  }
});
document.addEventListener('keydown', (e) => {
  if (e.key === 'Escape' && !document.querySelector('dialog[open]')) {
    document.querySelectorAll('.drawer-wrapper.is-open').forEach((d) => DrawerToggle.close(d.id));
  }
});

/* ---------- Header sticky / transparent ---------- */
(function () {
  const header = document.querySelector('.header-wrapper--transparent, .header-wrapper--sticky');
  if (!header) return;
  const onScroll = () => header.classList.toggle('is-scrolled', window.scrollY > 40);
  window.addEventListener('scroll', onScroll, { passive: true });
  onScroll();
})();

/* ---------- Recherche prédictive ---------- */
class PredictiveSearch extends HTMLElement {
  connectedCallback() {
    this._lifecycle?.abort();
    this._lifecycle = new AbortController();
    if (this.closest('[inert]')) return;
    this.input = this.querySelector('input[type="search"]');
    this.results = this.querySelector('.predictive-results');
    this.initial = this.querySelector('[data-search-initial]');
    this.clearButton = this.querySelector('[data-search-clear]');
    this.status = this.querySelector('[data-search-status]');
    this.minChars = Number(this.dataset.minChars || 2);
    this.cache = new Map();
    this.activeIndex = -1;
    this.controller = null;
    if (!this.input || !this.results) return;

    this.input.addEventListener('input', () => this.onInput(), { signal: this._lifecycle.signal });
    this.input.addEventListener('keydown', (event) => this.onKeydown(event), { signal: this._lifecycle.signal });
    this.clearButton?.addEventListener('click', () => this.clear(), { signal: this._lifecycle.signal });
    this.results.addEventListener('mousemove', (event) => {
      const option = event.target.closest('[data-search-option]');
      if (!option) return;
      this.setActive(this.options.indexOf(option));
    }, { signal: this._lifecycle.signal });
    this.toggleClearButton();
  }

  onInput() {
    window.clearTimeout(this.timer);
    this.toggleClearButton();
    this.activeIndex = -1;
    const query = this.input.value.trim();
    if (query.length < this.minChars) {
      this.resetResults();
      return;
    }
    this.timer = window.setTimeout(() => this.search(query), 240);
  }

  get options() {
    return Array.from(this.results.querySelectorAll('[data-search-option]'));
  }

  onKeydown(event) {
    if (!['ArrowDown', 'ArrowUp', 'Enter'].includes(event.key)) return;
    const options = this.options;
    if (!options.length) return;

    if (event.key === 'Enter' && this.activeIndex >= 0) {
      event.preventDefault();
      options[this.activeIndex].click();
      return;
    }

    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      const direction = event.key === 'ArrowDown' ? 1 : -1;
      const next = this.activeIndex + direction;
      this.setActive(next < 0 ? options.length - 1 : next % options.length);
    }
  }

  setActive(index) {
    const options = this.options;
    options.forEach((option, optionIndex) => {
      const selected = optionIndex === index;
      if (!option.id) option.id = 'search-option-' + optionIndex;
      option.classList.toggle('is-active', selected);
      option.setAttribute('aria-selected', String(selected));
    });
    this.activeIndex = index;
    const active = options[index];
    // La suggestion active est identifiable par le lecteur d'écran
    if (this.input) {
      if (active) this.input.setAttribute('aria-activedescendant', active.id);
      else this.input.removeAttribute('aria-activedescendant');
    }
    active?.scrollIntoView({ block: 'nearest' });
  }

  toggleClearButton() {
    if (this.clearButton) this.clearButton.hidden = this.input.value.length === 0;
  }

  setLoading(loading) {
    this.toggleAttribute('aria-busy', loading);
    if (loading) this.announce(this.dataset.loadingText || 'Loading');
  }

  announce(message) {
    if (this.status) this.status.textContent = message;
  }

  resetResults() {
    this.controller?.abort();
    this.results.innerHTML = '';
    this.results.hidden = true;
    this.initial?.removeAttribute('hidden');
    this.input.setAttribute('aria-expanded', 'false');
    this.announce('');
  }

  clear() {
    this.input.value = '';
    this.toggleClearButton();
    this.resetResults();
    this.input.focus();
  }

  render(html) {
    this.results.innerHTML = html;
    this.results.hidden = false;
    this.initial?.setAttribute('hidden', '');
    const count = this.options.length;
    this.input.setAttribute('aria-expanded', String(count > 0));
    this.announce(`${count} ${this.dataset.resultsText || 'results available'}`);
    this.activeIndex = -1;
  }

  async search(query = this.input.value.trim()) {
    if (query.length < this.minChars) {
      this.resetResults();
      return;
    }

    const cacheKey = query.toLocaleLowerCase();
    if (this.cache.has(cacheKey)) {
      this.render(this.cache.get(cacheKey));
      return;
    }

    this.controller?.abort();
    this.controller = new AbortController();
    const controller = this.controller;
    this.setLoading(true);

    try {
      const resourceLimits = {
        product: Number(this.dataset.productsLimit || 4),
        query: Number(this.dataset.queriesLimit || 0),
        collection: Number(this.dataset.collectionsLimit || 0),
        article: Number(this.dataset.articlesLimit || 0),
        page: Number(this.dataset.pagesLimit ?? 2)
      };
      const scope = (this.dataset.searchTypes || 'product,article,page').split(',');
      ['product', 'article', 'page'].forEach(type => { if (!scope.includes(type)) resourceLimits[type] = 0; });
      const resourceTypes = Object.entries(resourceLimits)
        .filter(([, limit]) => limit > 0)
        .map(([type]) => type);
      if (!resourceTypes.length) { this.resetResults(); return; }
      const params = new URLSearchParams({
        q: query,
        'resources[type]': resourceTypes.join(','),
        'resources[limit]': String(Math.max(...Object.values(resourceLimits))),
        'resources[limit_scope]': 'each',
        'resources[options][unavailable_products]': 'last',
        section_id: 'predictive-search'
      });
      const endpoint = window.themeSettings?.routes?.predictive_search || '/search/suggest';
      const res = await fetch(`${endpoint}?${params}`, {
        signal: controller.signal,
        headers: { Accept: 'text/html' }
      });
      if (!res.ok) throw new Error(`Predictive search failed: ${res.status}`);
      const html = await res.text();
      if (controller.signal.aborted || !this.isConnected) return;
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const inner = doc.querySelector('#predictive-search-results');
      if (!inner) throw new Error('Predictive search markup missing');
      if (query !== this.input.value.trim()) return;
      this.cache.set(cacheKey, inner.innerHTML);
      this.render(inner.innerHTML);
    } catch (error) {
      if (error.name === 'AbortError') return;
      this.results.hidden = true;
      this.initial?.removeAttribute('hidden');
      this.input.setAttribute('aria-expanded', 'false');
      this.announce(this.dataset.errorText || 'Search unavailable');
    } finally {
      if (this.controller === controller) this.setLoading(false);
    }
  }
  disconnectedCallback() {
    this._lifecycle?.abort(); this.controller?.abort(); clearTimeout(this.timer);
  }
}
customElements.define('predictive-search', PredictiveSearch);

class SearchModalController {
  constructor() {
    this.modal = null;
    this.panel = null;
    this.lastTrigger = null;
    this.onKeydown = this.onKeydown.bind(this);
  }

  open(trigger) {
    this.modal = document.getElementById('search-modal');
    this.panel = this.modal?.querySelector('.search-modal__panel');
    if (!this.modal) return;
    this.lastTrigger = trigger || document.activeElement;
    this.modal.classList.add('is-open');
    this.modal.setAttribute('aria-hidden', 'false');
    document.body.classList.add('search-open');
    document.addEventListener('keydown', this.onKeydown);
    const focusInput = () => {
      const search = this.modal.querySelector('predictive-search');
      const input = search?.input || this.modal.querySelector('input[type="search"]');
      input?.focus({ preventScroll: true });
      if (input?.value.trim().length >= (search?.minChars || 2)) search?.search();
    };
    this.focusFrame = window.requestAnimationFrame(focusInput);
    // La transition d'ouverture peut faire échouer le premier focus : on le confirme après coup
    this.focusTimer = window.setTimeout(() => {
      if (this.modal?.isConnected && this.modal.classList.contains('is-open') && !this.modal.contains(document.activeElement)) focusInput();
    }, 230);
  }

  close() {
    clearTimeout(this.focusTimer); cancelAnimationFrame(this.focusFrame);
    if (!this.modal?.classList.contains('is-open')) return;
    this.modal.classList.remove('is-open');
    this.modal.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('search-open');
    document.removeEventListener('keydown', this.onKeydown);
    this.lastTrigger?.focus();
  }

  onKeydown(event) {
    if (event.key === 'Escape') {
      event.preventDefault();
      this.close();
      return;
    }
    if (event.key !== 'Tab' || !this.panel) return;
    const focusable = Array.from(this.panel.querySelectorAll(
      'a[href], button:not([disabled]):not([hidden]), input:not([disabled]), [tabindex]:not([tabindex="-1"])'
    )).filter((element) => element.offsetParent !== null);
    if (!focusable.length) return;
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }
}

/* Rotation des exemples de recherches dans le bouton de l'en-tête */
class SearchRotator extends HTMLElement {
  connectedCallback() {
    this.terms = (this.dataset.terms || '').split('||').filter(Boolean);
    if (this.terms.length < 2 || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    // Largeur figée sur le terme le plus long pour éviter que la capsule danse
    const probe = document.createElement('span');
    probe.style.cssText = 'position:absolute;visibility:hidden;white-space:nowrap;font:inherit;';
    this.appendChild(probe);
    let max = 0;
    this.terms.forEach((t) => { probe.textContent = t; max = Math.max(max, probe.offsetWidth); });
    probe.remove();
    this.style.display = 'inline-block';
    this.style.minWidth = `${Math.ceil(max)}px`;
    this.index = 0;
    this.timer = window.setInterval(() => {
      this.index = (this.index + 1) % this.terms.length;
      this.classList.remove('is-swap');
      void this.offsetWidth;
      this.textContent = this.terms[this.index];
      this.classList.add('is-swap');
    }, 2600);
  }
  disconnectedCallback() {
    window.clearInterval(this.timer);
  }
}
customElements.define('search-rotator', SearchRotator);

Converto.searchModal = new SearchModalController();

document.addEventListener('click', (event) => {
  const trigger = event.target.closest('[data-search-open]');
  if (trigger) {
    event.preventDefault();
    Converto.searchModal.open(trigger);
    return;
  }
  if (event.target.closest('[data-search-close]')) {
    event.preventDefault();
    Converto.searchModal.close();
  }
});

/* ---------- Multicolonne : carrousel pleine largeur en boucle infinie ---------- */
class McCarousel extends HTMLElement {
  connectedCallback() {
    this._lifecycle?.abort();
    this._lifecycle = new AbortController();
    if (this.closest('[inert]')) return;
    this.track = this.querySelector('.multicolumn--carousel');
    if (!this.track || this.track.children.length < 2) return;
    // Une copie inerte de la série de chaque côté : les originaux restent au
    // centre et le défilement est libre dans les deux sens dès le départ.
    this.track.querySelectorAll(':scope > [data-mc-clone]').forEach(clone => clone.remove());
    this.originals = [...this.track.children];
    const makeClone = (el) => {
      const clone = el.cloneNode(true);
      clone.setAttribute('aria-hidden', 'true');
      clone.setAttribute('inert', '');
      clone.dataset.mcClone = 'true';
      [clone, ...clone.querySelectorAll('[id], [data-shopify-editor-block]')].forEach(el => { el.removeAttribute('id'); el.removeAttribute('data-shopify-editor-block'); });
      return clone;
    };
    this.originals.forEach((el) => this.track.appendChild(makeClone(el)));
    [...this.originals].reverse().forEach((el) => this.track.prepend(makeClone(el)));
    this.addEventListener('click', (e) => {
      if (e.target.closest('[data-mc-prev]')) this.step(-1);
      if (e.target.closest('[data-mc-next]')) this.step(1);
    }, { signal: this._lifecycle.signal });
    this.track.addEventListener('scroll', () => this.normalize(0.35, 1.65), { signal: this._lifecycle.signal, passive: true });
    this.center();
    window.addEventListener('resize', () => this.center(), { signal: this._lifecycle.signal, passive: true });
  }
  series() {
    return this.track.scrollWidth / 3;
  }
  center() {
    // Première carte originale centrée dans la fenêtre
    const first = this.originals[0];
    const trackRect = this.track.getBoundingClientRect();
    const firstRect = first.getBoundingClientRect();
    const left = firstRect.left - trackRect.left + this.track.scrollLeft;
    this.track.scrollLeft = left - (this.track.clientWidth - firstRect.width) / 2;
  }
  normalize(low, high) {
    const serie = this.series();
    if (this.track.scrollLeft < serie * low) this.track.scrollLeft += serie;
    else if (this.track.scrollLeft > serie * high) this.track.scrollLeft -= serie;
  }
  step(dir) {
    this.normalize(0.5, 1.5);
    const first = this.track.children[0];
    const gap = parseFloat(getComputedStyle(this.track).columnGap) || 24;
    const delta = first.getBoundingClientRect().width + gap;
    const smooth = window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth';
    this.track.scrollBy({ left: dir * delta, behavior: smooth });
  }
  disconnectedCallback() { this._lifecycle?.abort(); this.track?.querySelectorAll(':scope > [data-mc-clone]').forEach(clone => clone.remove()); }
}
customElements.define('mc-carousel', McCarousel);

/* ---------- Multicolonne : fenêtre « tout voir » ---------- */
document.addEventListener('click', (e) => {
  const openBtn = e.target.closest('[data-mc-dialog-open]');
  if (openBtn) {
    openBtn.closest('section')?.querySelector('dialog.mc-dialog')?.showModal();
    return;
  }
  if (e.target.closest('[data-mc-dialog-close]')) {
    e.target.closest('dialog')?.close();
    return;
  }
  if (e.target.matches?.('dialog.mc-dialog')) e.target.close();
});

/* ---------- Compte à rebours ---------- */
class CountdownTimer extends HTMLElement {
  connectedCallback() {
    this.digits = this.querySelector('[data-countdown-digits]');
    let end;
    if (this.dataset.endDate) {
      end = new Date(this.dataset.endDate).getTime();
    } else {
      // Mode "evergreen" : durée fixe mémorisée par session
      const key = `converto-countdown-${this.dataset.key || 'default'}`;
      end = parseInt(sessionStorage.getItem(key) || '0', 10);
      if (!end || end < Date.now()) {
        end = Date.now() + (parseInt(this.dataset.minutes || '15', 10) * 60000);
        sessionStorage.setItem(key, String(end));
      }
    }
    this.end = end;
    this.tick();
    this.timer = setInterval(() => this.tick(), 1000);
  }
  disconnectedCallback() { clearInterval(this.timer); }
  tick() {
    const diff = this.end - Date.now();
    if (diff <= 0) {
      clearInterval(this.timer);
      if (this.dataset.hideOnEnd === 'true') { this.hidden = true; return; }
      this.digits.textContent = this.dataset.expiredText || '';
      return;
    }
    const d = Math.floor(diff / 86400000);
    const h = Math.floor((diff % 86400000) / 3600000);
    const m = Math.floor((diff % 3600000) / 60000);
    const s = Math.floor((diff % 60000) / 1000);
    const u = window.themeSettings?.countdownUnits || { d: 'j', h: 'h', m: 'min', s: 's' };
    const pad = (n) => String(n).padStart(2, '0');
    this.digits.textContent = (d > 0 ? `${d}${u.d} ` : '') + `${pad(h)}${u.h} ${pad(m)}${u.m} ${pad(s)}${u.s}`;
  }
}
customElements.define('countdown-timer', CountdownTimer);

/* ---------- Avant / Après ---------- */
class BeforeAfter extends HTMLElement {
  connectedCallback() {
    this._lifecycle?.abort();
    this._lifecycle = new AbortController();
    if (this.closest('[inert]')) return;
    this.handle = this.querySelector('.before-after__handle');
    if (this.handle) {
      // Curseur annoncé aux aides techniques, valeur synchronisée à chaque geste
      if (!this.handle.hasAttribute('role')) this.handle.setAttribute('role', 'slider');
      this.handle.setAttribute('aria-valuemin', '0');
      this.handle.setAttribute('aria-valuemax', '100');
      this.handle.setAttribute('aria-valuenow', '50');
      if (!this.handle.hasAttribute('tabindex')) this.handle.tabIndex = 0;
    }
    const setPos = (pos) => {
      this.style.setProperty('--ba-pos', pos + '%');
      this.handle?.setAttribute('aria-valuenow', String(Math.round(pos)));
    };
    const move = (clientX) => {
      const rect = this.getBoundingClientRect();
      setPos(Math.min(100, Math.max(0, ((clientX - rect.left) / rect.width) * 100)));
    };
    const onPointerMove = (e) => move(e.clientX);
    this.addEventListener('pointerdown', (e) => {
      move(e.clientX);
      this.setPointerCapture(e.pointerId);
      this.addEventListener('pointermove', onPointerMove, { signal: this._lifecycle.signal });
    }, { signal: this._lifecycle.signal });
    this.addEventListener('pointerup', () => this.removeEventListener('pointermove', onPointerMove), { signal: this._lifecycle.signal });
    this.handle?.addEventListener('keydown', (e) => {
      const current = parseFloat(getComputedStyle(this).getPropertyValue('--ba-pos')) || 50;
      if (e.key === 'ArrowLeft') { e.preventDefault(); setPos(Math.max(0, current - 4)); }
      else if (e.key === 'ArrowRight') { e.preventDefault(); setPos(Math.min(100, current + 4)); }
      else if (e.key === 'Home') { e.preventDefault(); setPos(0); }
      else if (e.key === 'End') { e.preventDefault(); setPos(100); }
    }, { signal: this._lifecycle.signal });
  }
  disconnectedCallback() { this._lifecycle?.abort(); }
}
customElements.define('before-after', BeforeAfter);

/* ---------- Marquee : duplication du contenu pour boucler ---------- */


/* ---------- Fermer les <details> facettes au clic extérieur ---------- */
document.addEventListener('click', (e) => {
  document.querySelectorAll('.facets details[open]').forEach((d) => {
    if (!d.contains(e.target)) d.removeAttribute('open');
  });
});

/* ---------- F04 : lecteurs au clic et vidéos de fond hors écran ---------- */
Converto.playDeferredVideo = function (video) {
  if (video.error || video.dataset.loadFailed === 'true') {
    delete video.dataset.loadFailed;
    video.load();
  }
  return new Promise((resolve, reject) => {
    const sources = [...video.querySelectorAll('source')].filter((source) => !source.type || video.canPlayType(source.type));
    const failed = new Set();
    const cleanup = () => video.removeEventListener('error', onError, true);
    const onError = (event) => {
      if (event.target !== video) {
        if (sources.includes(event.target)) failed.add(event.target);
        if (failed.size < sources.length) return;
      }
      // Certains navigateurs ne rejettent pas play() quand tous les <source> échouent.
      video.dataset.loadFailed = 'true';
      cleanup();
      reject(new Error('Media source unavailable'));
      video.pause();
    };
    video.addEventListener('error', onError, true);
    Promise.resolve(video.play()).then(() => { cleanup(); resolve(); }, (error) => { cleanup(); reject(error); });
  });
};

/* Démarrage d'une vidéo sortie de son template, appelé DANS la tâche du clic.
   WebKit (Safari iOS, Chrome iOS, navigateurs intégrés aux applis) rattache
   l'autorisation de lecture au chargement déclenché par le geste : sans load()
   explicite, un élément inséré à l'instant et en preload="none" reste muet et
   en pause. Et quand le son reste refusé, on repart en muet plutôt que de ne
   rien jouer du tout, les contrôles natifs permettant de le rétablir. */
Converto.startVideo = function (video) {
  video.setAttribute('playsinline', '');
  video.setAttribute('webkit-playsinline', '');
  video.controls = true;
  video.muted = false;
  if (video.readyState === 0) video.load();
  return Converto.playDeferredVideo(video).catch((error) => {
    if (error && error.name === 'NotAllowedError') {
      video.muted = true;
      return Converto.playDeferredVideo(video);
    }
    throw error;
  });
};

document.addEventListener('click', async (e) => {
  const card = e.target.closest('.video-card');
  if (!card) return;
  if (card.dataset.videoId && !card.dataset.loaded) {
    card.dataset.loaded = 'true';
    const iframe = document.createElement('iframe');
    iframe.src = card.dataset.videoType === 'youtube'
      ? 'https://www.youtube.com/embed/' + encodeURIComponent(card.dataset.videoId) + '?autoplay=1&rel=0'
      : 'https://player.vimeo.com/video/' + encodeURIComponent(card.dataset.videoId) + '?autoplay=1';
    iframe.title = card.querySelector('button')?.getAttribute('aria-label') || '';
    iframe.allow = 'autoplay; encrypted-media; picture-in-picture';
    iframe.allowFullscreen = true;
    iframe.className = 'video-card__external';
    card.replaceChildren(iframe);
    return;
  }
  const template = card.querySelector('template[data-video-template]');
  if (template) template.replaceWith(template.content.cloneNode(true));
  const video = card.querySelector('video');
  if (!video || card.hasAttribute('aria-busy')) return;
  const play = card.querySelector('.video-card__play');
  if (video.controls && e.target === video) return;
  if (video.paused) {
    document.querySelectorAll('.video-card video').forEach((other) => {
      if (other === video || other.paused) return;
      other.pause();
      const otherPlay = other.closest('.video-card').querySelector('.video-card__play');
      if (otherPlay) otherPlay.hidden = false;
    });
    card.setAttribute('aria-busy', 'true');
    try {
      await Converto.startVideo(video);
      if (play) {
        if (play.contains(document.activeElement)) {
          video.tabIndex = 0;
          video.focus({ preventScroll: true });
        }
        play.hidden = true;
      }
    } catch (error) {
      if (play) play.hidden = false;
      if (error.name !== 'AbortError' && card.isConnected && card.dataset.videoError) Converto.toast(card.dataset.videoError, { alert: true });
    } finally {
      card.removeAttribute('aria-busy');
    }
  } else {
    video.pause();
    if (play) play.hidden = false;
  }
});

class BackgroundVideo extends HTMLElement {
  connectedCallback() {
    this.events = new AbortController();
    this.motion = matchMedia('(prefers-reduced-motion: reduce)');
    this.button = this.querySelector('.background-video__toggle');
    if (!this.button) return;
    this.button.hidden = false;
    this.button.addEventListener('click', () => {
      this.userPaused = !this.video || this.video.paused ? false : true;
      if (this.userPaused) this.video.pause();
      else this.play();
    }, { signal: this.events.signal });
    this.motion.addEventListener('change', () => {
      if (this.motion.matches) this.video?.pause();
      else if (this.visible && !this.userPaused) this.autoplay();
    }, { signal: this.events.signal });
    if ('IntersectionObserver' in window) {
      this.observer = new IntersectionObserver((entries) => {
        this.visible = entries.some((entry) => entry.isIntersecting);
        if (this.visible) this.autoplay();
        else this.video?.pause();
      }, { threshold: 0.01 });
      this.observer.observe(this);
    } else {
      // Sans observation, la couverture et le bouton restent utilisables.
      this.visible = true;
    }
    if (this.video) this.bindVideo();
  }

  autoplay() {
    if (!this.userPaused && !this.motion.matches && !navigator.connection?.saveData) this.play();
  }

  async play() {
    if (!this.isConnected) return;
    if (!this.video) {
      const template = this.querySelector('template[data-background-template]');
      if (!template) return;
      template.replaceWith(template.content.cloneNode(true));
      this.video = this.querySelector('video');
      if (!this.video) return;
      this.video.muted = true;
      this.bindVideo();
    }
    try { await Converto.playDeferredVideo(this.video); }
    catch (error) { this.sync(); } // Autoplay refusé : couverture et bouton disponibles.
  }

  bindVideo() {
    ['playing', 'pause', 'error', 'ended'].forEach((event) => {
      this.video.addEventListener(event, () => this.sync(), { signal: this.events.signal });
    });
    this.sync();
  }

  sync() {
    const playing = this.video && !this.video.paused && !this.video.error;
    this.classList.toggle('is-playing', !!playing);
    this.button.setAttribute('aria-label', playing ? this.dataset.pauseLabel : this.dataset.playLabel);
    this.querySelector('[data-background-play]').hidden = !!playing;
    this.querySelector('[data-background-pause]').hidden = !playing;
  }

  disconnectedCallback() {
    this.observer?.disconnect();
    this.video?.pause();
    this.events?.abort();
  }
}
customElements.define('background-video', BackgroundVideo);


/* ---------- Timeline animée : progression du trait au défilement ---------- */
class TimelineScroll extends HTMLElement {
  connectedCallback() {
    this.list = this.querySelector('.tl-list');
    this.steps = [...this.querySelectorAll('[data-tl-step]')];
    this.markers = this.steps.map((s) => s.querySelector('.tl-step__marker'));
    if (!this.list || !this.markers.length) return;
    this.reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    this._request = this.requestUpdate.bind(this);
    window.addEventListener('scroll', this._request, { passive: true });
    window.addEventListener('resize', this._request);
    this.requestUpdate();
  }

  disconnectedCallback() {
    cancelAnimationFrame(this._raf); this._raf = null;
    window.removeEventListener('scroll', this._request);
    window.removeEventListener('resize', this._request);
  }

  requestUpdate() {
    if (this._raf) return;
    this._raf = requestAnimationFrame(() => {
      this._raf = null;
      this.update();
    });
  }

  update() {
    if (this.reduceMotion.matches) {
      this.list.style.setProperty('--timeline-progress', '100%');
      this.steps.forEach((step) => step.classList.add('is-reached'));
      return;
    }
    const anchor = window.innerHeight * 0.58;
    const first = this.markers[0].getBoundingClientRect();
    const last = this.markers[this.markers.length - 1].getBoundingClientRect();
    const firstCenter = first.top + first.height / 2;
    const lastCenter = last.top + last.height / 2;
    const progress = Math.min(Math.max((anchor - firstCenter) / Math.max(lastCenter - firstCenter, 1), 0), 1);
    this.list.style.setProperty('--timeline-progress', (progress * 100).toFixed(1) + '%');
    this.markers.forEach((marker, index) => {
      const rect = marker.getBoundingClientRect();
      this.steps[index].classList.toggle('is-reached', rect.top + rect.height / 2 <= anchor + 2);
    });
  }
}
customElements.define('timeline-scroll', TimelineScroll);

/* ---------- Routine recommandée : ajout direct par variante ---------- */
document.addEventListener('click', async (e) => {
  const btn = e.target.closest('[data-routine-add]');
  if (!btn || btn.disabled || btn.classList.contains('is-loading')) return;
  Converto.lockBtn(btn);
  /* Avec la liste déroulante, la variante vit dans l'input du <card-offers>. */
  const offerInput = btn.closest('card-offers')?.querySelector('[data-offer-input]');
  const variantId = parseInt(offerInput?.value || btn.dataset.variantId, 10);
  try {
    await Converto.cart.add(
      { id: variantId, quantity: 1 },
      { openDrawer: btn.dataset.openDrawer === 'true' }
    );
    btn.classList.add('is-added');
    const sub = btn.querySelector('[data-routine-sub]');
    if (sub && btn.dataset.addedText) sub.textContent = btn.dataset.addedText;
    const icon = btn.querySelector('.rcs-add__icon');
    if (icon) icon.textContent = '✓';
  } catch (err) { /* toast d'erreur géré par Converto.cart.add */ }
  Converto.unlockBtn(btn);
});

/* ---------- Copie de code promo ---------- */
document.addEventListener('click', async (e) => {
  const btn = e.target.closest('[data-copy-code]');
  if (!btn || btn.closest('converto-popup')) return;
  try {
    await navigator.clipboard.writeText(btn.dataset.copyCode);
    const copiedText = btn.dataset.copiedText;
    if (copiedText) Converto.toast(copiedText);
  } catch (err) { /* clipboard indisponible */ }
});

/* ---------- Newsletter : message de succès après redirection ---------- */
if (window.location.search.includes('customer_posted=true')) {
  document.querySelectorAll('[data-newsletter-success]').forEach((el) => { el.hidden = false; });
}

/* ---------- Stories vidéos natives (bulles + surimpression) ---------- */
class ProductStories extends HTMLElement {
  connectedCallback() {
    this._lifecycle?.abort();
    this._lifecycle = new AbortController();
    if (this.closest('[inert]')) return;
    this.viewer = this.querySelector('[data-story-viewer]') || this.viewer;
    if (!this.viewer) return;
    this.buttons = [...this.querySelectorAll('[data-story-open]')];
    this.index = 0;
    // Le viewer vit dans <body> : un ancêtre animé (transform, will-change)
    // ferait sinon d'un position:fixed un cadre local et casserait l'overlay.
    document.body.appendChild(this.viewer);
    this.stage = this.viewer.querySelector('[data-story-stage]');
    this.titleEl = this.viewer.querySelector('[data-story-title]');
    this.addEventListener('click', (e) => {
      const open = e.target.closest('[data-story-open]');
      if (open) this.open(parseInt(open.dataset.storyOpen, 10));
    }, { signal: this._lifecycle.signal });
    this.viewer.addEventListener('click', (e) => {
      if (e.target.closest('[data-story-close]')) return this.close();
      if (e.target.closest('[data-story-prev]')) return this.open(this.index - 1);
      if (e.target.closest('[data-story-next]')) return this.open(this.index + 1);
      if (e.target === this.viewer) this.close();
    }, { signal: this._lifecycle.signal });
    this._onKey = (e) => {
      if (!this.viewer || this.viewer.hidden) return;
      if (e.key === 'Escape') this.close();
      if (e.key === 'ArrowLeft') this.open(this.index - 1);
      if (e.key === 'ArrowRight') this.open(this.index + 1);
    };
    document.addEventListener('keydown', this._onKey, { signal: this._lifecycle.signal });
  }
  disconnectedCallback() {
    this._lifecycle?.abort();
    document.removeEventListener('keydown', this._onKey);
    if (this.viewer) {
      if (!this.viewer.hidden) { this.stage?.querySelector('video')?.pause(); this.stage?.replaceChildren(); this.viewer.hidden = true; document.body.style.overflow = ''; }
      this.viewer.remove();
    }
  }
  open(i) {
    const count = this.buttons.length;
    if (!count) return;
    this.index = (i + count) % count;
    const tpl = this.querySelector(`[data-story-template="${this.index}"]`);
    if (!tpl) return;
    this.stage.innerHTML = '';
    this.stage.appendChild(tpl.content.cloneNode(true));
    const btn = this.buttons.find((b) => parseInt(b.dataset.storyOpen, 10) === this.index);
    this.titleEl.textContent = btn ? btn.dataset.storyTitle || '' : '';
    this.viewer.hidden = false;
    document.body.style.overflow = 'hidden';
    const video = this.stage.querySelector('video');
    if (video) {
      video.play().catch(() => {});
      // Fin de la dernière story : fermeture ; sinon story suivante
      video.addEventListener('ended', () => {
        if (this.index + 1 >= count) this.close();
        else this.open(this.index + 1);
      }, { signal: this._lifecycle.signal, once: true });
    }
    this.viewer.querySelector('[data-story-close]').focus();
  }
  close() {
    const video = this.stage.querySelector('video');
    if (video) video.pause();
    this.stage.innerHTML = '';
    this.viewer.hidden = true;
    document.body.style.overflow = '';
    const btn = this.buttons.find((b) => parseInt(b.dataset.storyOpen, 10) === this.index);
    if (btn) btn.focus();
  }
}
customElements.define('product-stories', ProductStories);

/* ---------- F06 : initialisations limitées à la section et nettoyables ---------- */
(() => {
  const bindings = new Map();
  const reveal = 'IntersectionObserver' in window ? new IntersectionObserver(entries => {
    entries.forEach(entry => {
      if (entry.isIntersecting) { entry.target.classList.add('is-inview'); reveal.unobserve(entry.target); }
    });
  }, { rootMargin: '0px 0px -8% 0px' }) : null;
  const within = (root, selector) => [...(root.matches?.(selector) ? [root] : []), ...root.querySelectorAll(selector)];
  const bind = (element, key, setup) => {
    let entry = bindings.get(element);
    if (!entry) { entry = new Map(); bindings.set(element, entry); }
    if (entry.has(key)) return;
    const events = new AbortController();
    const cleanup = setup({ signal: events.signal });
    entry.set(key, () => { events.abort(); cleanup?.(); });
  };
  Converto.destroySectionFeatures = root => {
    bindings.forEach((entry, element) => {
      if (!element.isConnected || root === element || root.contains?.(element)) { entry.forEach(cleanup => cleanup()); bindings.delete(element); }
    });
  };
  Converto.initSectionFeatures = (root = document) => {
    bindings.forEach((entry, element) => { if (!element.isConnected) { entry.forEach(cleanup => cleanup()); bindings.delete(element); } });
    within(root, '[data-drawer-open]').forEach(button => { if (!button.hasAttribute('aria-expanded')) button.setAttribute('aria-expanded', 'false'); });
    within(root, 'input[data-rotate-placeholder]').forEach(input => bind(input, 'placeholder', () => {
      const terms = (input.dataset.rotatePlaceholder || '').split('||').filter(Boolean);
      if (terms.length < 2 || matchMedia('(prefers-reduced-motion: reduce)').matches) return;
      let index = -1;
      const timer = setInterval(() => {
        if (input.value || document.activeElement === input) return;
        index = (index + 1) % (terms.length + 1);
        input.placeholder = index === terms.length ? (input.dataset.rotateDefault || '') : (input.dataset.rotateTemplate || '[term]').replace('[term]', terms[index]);
      }, 2600);
      return () => clearInterval(timer);
    }));
    within(root, '.footer-nav-group').forEach(group => bind(group, 'footer', options => {
      const mq = matchMedia('(max-width: 649px)');
      const apply = () => { group.open = !mq.matches; };
      apply(); mq.addEventListener('change', apply, options);
      group.querySelector('summary')?.addEventListener('click', event => { if (!mq.matches) event.preventDefault(); }, options);
    }));
    within(root, '.marquee').forEach(marquee => bind(marquee, 'marquee', () => {
      const content = marquee.querySelector('.marquee__content');
      if (!content || marquee.children.length > 1) return;
      const clone = content.cloneNode(true);
      clone.setAttribute('aria-hidden', 'true'); clone.setAttribute('inert', '');
      [clone, ...clone.querySelectorAll('[id], [data-shopify-editor-block]')].forEach(el => { el.removeAttribute('id'); el.removeAttribute('data-shopify-editor-block'); });
      marquee.appendChild(clone);
      return () => clone.remove();
    }));
    within(root, '.product-tabs').forEach(tabsRoot => bind(tabsRoot, 'tabs', options => {
      const tabs = [...tabsRoot.querySelectorAll('.product-tabs__btn')];
      const panels = [...tabsRoot.querySelectorAll('.product-tabs__panel')];
      if (!tabs.length) return;
      const select = (button, focus = false) => {
        tabs.forEach(tab => { tab.setAttribute('aria-selected', String(tab === button)); tab.tabIndex = tab === button ? 0 : -1; });
        panels.forEach(panel => { panel.hidden = panel.id !== button.getAttribute('aria-controls'); });
        if (focus) button.focus();
      };
      select(tabs.find(tab => tab.getAttribute('aria-selected') === 'true') || tabs[0]);
      tabsRoot.addEventListener('click', event => { const tab = event.target.closest('.product-tabs__btn'); if (tabs.includes(tab)) select(tab); }, options);
      tabsRoot.addEventListener('keydown', event => {
        const index = tabs.indexOf(event.target);
        if (index < 0) return;
        const next = { ArrowRight: (index + 1) % tabs.length, ArrowLeft: (index - 1 + tabs.length) % tabs.length, Home: 0, End: tabs.length - 1 }[event.key];
        if (next === undefined) return;
        event.preventDefault(); select(tabs[next], true);
      }, options);
      tabsRoot.addEventListener('shopify:block:select', event => {
        const tab = event.target.closest('.product-tabs__btn') || tabs.find(item => item.dataset.blockId === event.detail?.blockId);
        if (!tabs.includes(tab)) return;
        select(tab);
        const details = tabsRoot.querySelectorAll('.product-tabs__accordions details');
        details.forEach((item, index) => { item.open = index === tabs.indexOf(tab); });
      }, options);
    }));
    if (document.body.hasAttribute('data-anim-reveal')) {
      const observe = element => bind(element, 'reveal', () => {
        if (!reveal || window.Shopify?.designMode || matchMedia('(prefers-reduced-motion: reduce)').matches) element.classList.add('is-inview');
        else reveal.observe(element);
        return () => reveal?.unobserve(element);
      });
      within(root, '.fade-in').forEach(observe);
      within(root, '.section-header, .grid, .slider, .stats, .accordion, .steps, .custom-content, .image-with-text, .pillars, .footer__grid').forEach(parent => {
        if (parent.closest('.header-wrapper, .drawer, .popup, [data-drawer-close], product-gallery, product-stories, .gallery-widget, .mc-dialog')) return;
        const children = parent.classList.contains('section-header') ? [parent] : [...parent.children];
        children.forEach((child, index) => { child.classList.add('anim-reveal'); child.style.setProperty('--anim-delay', Math.min(index * 70, 420) + 'ms'); observe(child); });
      });
    }
  };
  Converto.initSectionFeatures();
  document.addEventListener('shopify:section:load', event => Converto.initSectionFeatures(event.target));
  document.addEventListener('shopify:section:unload', event => {
    const root = event.target;
    Converto.destroySectionFeatures(root);
    if (Converto.searchModal?.modal && root.contains(Converto.searchModal.modal)) Converto.searchModal.close();
    root.querySelectorAll('video').forEach(video => video.pause());
    root.querySelectorAll('dialog[open]').forEach(dialog => dialog.close());
    root.querySelectorAll('.drawer-wrapper.is-open').forEach(drawer => Converto.drawer.close(drawer.id));
    document.querySelectorAll('.lightbox').forEach(lightbox => { if (root.contains(lightbox._source)) lightbox._dismiss?.(); });
  });
  document.addEventListener('cart:state', () => Converto.initSectionFeatures());
})();

/* ---------- Filtres par besoin de la collection en vedette ----------
   Les chips sont des liens vers la collection filtrée (repli sans JS et SEO) ;
   avec JS, la grille est remplacée par les cartes de la page filtrée. */
class FeaturedCollectionFilter extends HTMLElement {
  connectedCallback() {
    this.grid = this.querySelector('[data-fc-grid]');
    this._onClick = (e) => this.filter(e);
    this.addEventListener('click', this._onClick);
    this.fillCounts();
  }
  /* Compteurs de la boussole : lus une fois sur la page collection (facettes),
     hors fiche il n'y a pas de collection.filters en Liquid. */
  async fillCounts() {
    const compass = this.querySelector('[data-fc-count-template]');
    if (!compass || this._counted) return;
    this._counted = true;
    try {
      const res = await fetch(compass.dataset.fcCollectionUrl, { cache: 'no-store' });
      if (!res.ok) return;
      const doc = new DOMParser().parseFromString(await res.text(), 'text/html');
      compass.querySelectorAll('[data-fc-count]').forEach((el) => {
        const input = Array.from(doc.querySelectorAll('#facets-form input[type="checkbox"]'))
          .find((i) => i.value === el.dataset.fcCount);
        const match = input?.closest('label')?.textContent.match(/\((\d+)\)/);
        if (match) {
          el.textContent = compass.dataset.fcCountTemplate.replace('999999', match[1]);
          el.hidden = false;
        }
      });
    } catch (e) { /* compteurs facultatifs */ }
  }
  disconnectedCallback() {
    this.abort?.abort();
    this.removeEventListener('click', this._onClick);
  }
  async filter(e) {
    const chip = e.target.closest('[data-fc-filter]');
    if (!chip || !this.grid) return;
    if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
    e.preventDefault();
    this.abort?.abort();
    const abort = new AbortController();
    this.abort = abort;
    this.querySelectorAll('[data-fc-filter]').forEach((c) => {
      c.classList.toggle('is-active', c === chip);
      c.setAttribute('aria-current', c === chip ? 'true' : 'false');
    });
    this.grid.setAttribute('aria-busy', 'true');
    try {
      const res = await fetch(chip.href, { signal: abort.signal });
      if (!res.ok) throw new Error('collection filter');
      const doc = new DOMParser().parseFromString(await res.text(), 'text/html');
      if (abort.signal.aborted || !this.isConnected) return;
      const cards = Array.from(doc.querySelectorAll('.collection-main .product-card'))
        .slice(0, Number(this.dataset.limit) || 8);
      if (cards.length) {
        // Cellule modèle clonée : mêmes classes (dont l'état révélé de
        // l'animation) et attributs que la disposition d'origine.
        const model = this.grid.querySelector(':scope > *');
        this.grid.replaceChildren(...cards.map((card) => {
          const cell = model ? model.cloneNode(false) : document.createElement('div');
          if (!model) cell.className = 'fade-in';
          cell.classList.add('anim-reveal', 'is-inview');
          cell.appendChild(card);
          return cell;
        }));
        Converto.ensureQuickBuy?.(this.grid);
      } else {
        // État vide de la collection réutilisé (message déjà traduit)
        const empty = doc.querySelector('.collection-main .cart-empty') || document.createElement('p');
        this.grid.replaceChildren(empty);
      }
    } catch (err) {
      // Repli : navigation classique vers la collection filtrée
      if (err.name !== 'AbortError') window.location.href = chip.href;
    } finally {
      this.grid.removeAttribute('aria-busy');
    }
  }
}
customElements.define('featured-collection-filter', FeaturedCollectionFilter);

/* ---------- Sélecteur d'offres d'une carte produit ---------- */
/* Les tuiles pilotent le champ caché du formulaire d'ajout : une seule requête
   panier, quelle que soit la variante choisie. Les noms de groupe sont rendus
   uniques ici, car un même produit peut apparaître deux fois sur la page. */
let cardOffersCount = 0;
class CardOffers extends HTMLElement {
  connectedCallback() {
    this.input = this.querySelector('[data-offer-input]');
    this.submit = this.querySelector('button[type="submit"]');
    this.picker = this.querySelector('[data-offer-picker]');
    this.radios = [...this.querySelectorAll('input[type="radio"]')];
    if (!this.input) return;
    if (this.picker) { this.bindPicker(); return; }
    if (!this.radios.length) return;
    const group = `card-offer-${++cardOffersCount}`;
    this.radios.forEach(radio => { radio.name = group; });
    this._onChange = event => this.select(event.target);
    this.addEventListener('change', this._onChange);
    const checked = this.radios.find(radio => radio.checked);
    if (checked) this.select(checked, true);
  }
  select(radio, silent = false) {
    if (!radio || radio.type !== 'radio') return;
    this.radios.forEach(item => item.closest('.card-offer')?.classList.toggle('is-selected', item === radio));
    this.input.value = radio.value;
    if (this.submit) this.submit.disabled = radio.disabled;
    if (silent) return;
    this.dispatchEvent(new CustomEvent('offer:changed', { bubbles: true, detail: { variantId: radio.value } }));
  }
  /* Sélecteur maison : le panneau est sorti dans <body> à l'ouverture, sinon le
     rail du carrousel, qui masque son débordement, le couperait. */
  bindPicker() {
    this.trigger = this.picker.querySelector('[data-offer-trigger]');
    this.panneau = this.picker.querySelector('[data-offer-options]');
    this.etiquette = this.picker.querySelector('[data-offer-current]');
    this.options = [...this.panneau.querySelectorAll('[role="option"]')];
    if (!this.trigger || !this.options.length) return;
    this._pickerAbort = new AbortController();
    const signal = this._pickerAbort.signal;
    this.appliquer(this.options.find((o) => o.getAttribute('aria-selected') === 'true') || this.options[0], true);
    this.trigger.addEventListener('click', () => this.basculer(), { signal });
    this.panneau.addEventListener('click', (event) => {
      const option = event.target.closest('[role="option"]');
      if (!option || option.getAttribute('aria-disabled') === 'true') return;
      this.appliquer(option);
      this.fermer(true);
    }, { signal });
    this.trigger.addEventListener('keydown', (event) => {
      if (event.key === 'ArrowDown' || event.key === 'ArrowUp') { event.preventDefault(); this.ouvrir(); this.deplacer(event.key === 'ArrowDown' ? 1 : -1); }
    }, { signal });
    this.panneau.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') { event.preventDefault(); this.fermer(true); }
      else if (event.key === 'ArrowDown') { event.preventDefault(); this.deplacer(1); }
      else if (event.key === 'ArrowUp') { event.preventDefault(); this.deplacer(-1); }
      else if (event.key === 'Enter' || event.key === ' ') { event.preventDefault(); const a = this.options[this.curseur]; if (a && a.getAttribute('aria-disabled') !== 'true') { this.appliquer(a); this.fermer(true); } }
    }, { signal });
    document.addEventListener('pointerdown', (event) => {
      if (!this.ouvert) return;
      if (this.picker.contains(event.target) || this.panneau.contains(event.target)) return;
      this.fermer();
    }, { signal, capture: true });
    window.addEventListener('resize', () => this.fermer(), { signal });
    window.addEventListener('scroll', () => this.fermer(), { signal, passive: true });
  }
  appliquer(option, silencieux = false) {
    if (!option) return;
    this.options.forEach((o) => { o.setAttribute('aria-selected', String(o === option)); o.classList.toggle('is-selected', o === option); });
    this.curseur = this.options.indexOf(option);
    const libelle = option.querySelector('.card-offers__option-label')?.textContent.trim() || '';
    const sous = option.querySelector('.card-offers__option-sub')?.textContent.trim() || '';
    const prix = option.querySelector('.card-offers__option-price')?.textContent.trim() || '';
    this.etiquette.textContent = sous ? `${libelle} (${sous}) · ${prix}` : `${libelle} · ${prix}`;
    this.input.value = option.dataset.value;
    if (this.submit) this.submit.disabled = option.getAttribute('aria-disabled') === 'true';
    if (silencieux) return;
    this.dispatchEvent(new CustomEvent('offer:changed', { bubbles: true, detail: { variantId: option.dataset.value } }));
  }
  deplacer(pas) {
    const total = this.options.length;
    let i = typeof this.curseur === 'number' ? this.curseur : 0;
    for (let n = 0; n < total; n += 1) {
      i = (i + pas + total) % total;
      if (this.options[i].getAttribute('aria-disabled') !== 'true') break;
    }
    this.curseur = i;
    this.panneau.setAttribute('aria-activedescendant', this.options[i].id);
    this.options.forEach((o, n) => o.classList.toggle('is-cursor', n === i));
    this.options[i].scrollIntoView({ block: 'nearest' });
  }
  placer() {
    const r = this.trigger.getBoundingClientRect();
    const sousLeDeclencheur = window.innerHeight - r.bottom > 220 || r.top < 240;
    this.panneau.style.width = `${Math.round(r.width)}px`;
    this.panneau.style.left = `${Math.round(r.left)}px`;
    if (sousLeDeclencheur) { this.panneau.style.top = `${Math.round(r.bottom + 6)}px`; this.panneau.style.bottom = 'auto'; }
    else { this.panneau.style.top = 'auto'; this.panneau.style.bottom = `${Math.round(window.innerHeight - r.top + 6)}px`; }
  }
  ouvrir() {
    if (this.ouvert) return;
    this.ouvert = true;
    if (this.panneau.parentElement !== document.body) document.body.append(this.panneau);
    this.panneau.hidden = false;
    this.panneau.classList.add('is-open');
    this.trigger.setAttribute('aria-expanded', 'true');
    this.placer();
    this.panneau.setAttribute('tabindex', '-1');
    this.panneau.focus({ preventScroll: true });
    this.deplacer(0);
  }
  fermer(rendreFocus = false) {
    if (!this.ouvert) return;
    this.ouvert = false;
    this.panneau.hidden = true;
    this.panneau.classList.remove('is-open');
    this.trigger.setAttribute('aria-expanded', 'false');
    if (rendreFocus) this.trigger.focus();
  }
  basculer() { this.ouvert ? this.fermer(true) : this.ouvrir(); }
  disconnectedCallback() {
    this.removeEventListener('change', this._onChange);
    this._pickerAbort?.abort();
    if (this.panneau && this.panneau.parentElement === document.body) this.panneau.remove();
  }
}
customElements.define('card-offers', CardOffers);
